import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { generateVehicleSlug } from '@/lib/slug';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import './home.css';

const PAGE_SIZE = 5;

const formatPrice = (price: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

function getPlateFinal(plaque?: string): string | null {
  if (!plaque?.trim()) return null;
  const digits = plaque.replace(/\D/g, '');
  if (!digits) return null;
  return digits[digits.length - 1];
}

function vehicleTitle(vehicle: any) {
  return [vehicle.brand, vehicle.model, vehicle.version].filter(Boolean).join(' ');
}

function buildFallbackSlug(vehicle: any) {
  const brand = (vehicle.brand || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-');
  const model = (vehicle.model || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-');
  return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
}

function MosaicCard({
  vehicle,
  size = 'sm',
}: {
  vehicle: any;
  size?: 'lg' | 'sm';
}) {
  const [slug, setSlug] = useState(() => buildFallbackSlug(vehicle));

  useEffect(() => {
    let active = true;
    generateVehicleSlug(vehicle)
      .then((next) => {
        if (active && next) setSlug(next);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [vehicle]);

  const plateFinal = size === 'lg' ? getPlateFinal(vehicle.plaque) : null;
  const imageUrl = vehicle.photos?.[0];
  const optimized = imageUrl
    ? generateResponsiveImageUrls(imageUrl, size === 'lg' ? 720 : 420, size === 'lg' ? 520 : 280)
    : null;
  const loading = getImageLoadingStrategy(true, false);
  const year = vehicle.modelYear || vehicle.manufactureYear;
  const title = vehicleTitle(vehicle);

  return (
    <Link
      to={`/estoque/${slug}`}
      className={`wl7-mosaic-card wl7-mosaic-card--${size}`}
      aria-label={`${title} — ${formatPrice(vehicle.price)}`}
    >
      <div className="wl7-mosaic-card__media">
        {optimized ? (
          <img
            src={optimized.src}
            srcSet={optimized.srcSet}
            sizes={size === 'lg' ? '(max-width: 900px) 100vw, 48vw' : '(max-width: 900px) 50vw, 24vw'}
            alt={title}
            className="wl7-mosaic-card__img"
            loading={loading.loading}
            decoding="async"
            width={optimized.width}
            height={optimized.height}
          />
        ) : (
          <div className="wl7-mosaic-card__img wl7-mosaic-card__img--empty" />
        )}

        <span className="wl7-mosaic-card__price">{formatPrice(vehicle.price)}</span>

        {plateFinal && (
          <span className="wl7-mosaic-card__plate">Placa final: {plateFinal}</span>
        )}
      </div>

      <div className="wl7-mosaic-card__body">
        <h3 className="wl7-mosaic-card__title">{title}</h3>
        {year ? <p className="wl7-mosaic-card__year">{year}</p> : null}
      </div>
    </Link>
  );
}

export interface HomeVehicleMosaicProps {
  id?: string;
  title: string;
  lead?: string;
  vehicles: any[];
  viewAllHref?: string;
  viewAllLabel?: string;
  maxItems?: number;
  headerAction?: ReactNode;
  nextLabel?: string;
  prevLabel?: string;
}

export function HomeVehicleMosaic({
  id,
  title,
  lead,
  vehicles,
  viewAllHref = '/estoque',
  viewAllLabel = 'Ver estoque completo',
  maxItems = 20,
  headerAction,
  nextLabel = 'Próximos',
  prevLabel = 'Anteriores',
}: HomeVehicleMosaicProps) {
  const pool = useMemo(() => vehicles.slice(0, maxItems), [vehicles, maxItems]);
  const pageCount = Math.max(1, Math.ceil(pool.length / PAGE_SIZE));
  const [page, setPage] = useState(0);
  const safePage = Math.min(page, pageCount - 1);
  const titleId = id ? `${id}-title` : undefined;

  useEffect(() => {
    setPage(0);
  }, [pool.length, title]);

  const pageVehicles = useMemo(() => {
    const start = safePage * PAGE_SIZE;
    return pool.slice(start, start + PAGE_SIZE);
  }, [pool, safePage]);

  if (pool.length === 0) return null;

  const count = pageVehicles.length;
  const useMosaic = count >= 5;
  const featured = useMosaic ? pageVehicles[0] : null;
  const grid = useMosaic ? pageVehicles.slice(1, 5) : pageVehicles;
  const goNext = () => setPage((p) => (p + 1) % pageCount);
  const goPrev = () => setPage((p) => (p - 1 + pageCount) % pageCount);
  const uniformCols = Math.min(count, 4);

  return (
    <section
      id={id}
      className="wl7-new-arrivals"
      aria-labelledby={titleId}
    >
      <div className="wl7-container">
        <header className="wl7-new-arrivals__head">
          <div className="wl7-new-arrivals__head-row">
            <div>
              <h2 id={titleId} className="wl7-new-arrivals__title">
                {title}
              </h2>
              {lead && <p className="wl7-new-arrivals__lead">{lead}</p>}
            </div>
            {headerAction}
          </div>
        </header>

        <div className="wl7-new-arrivals__stage">
          {useMosaic ? (
            <div className="wl7-new-arrivals__grid">
              {featured && <MosaicCard vehicle={featured} size="lg" />}
              <div className="wl7-new-arrivals__cells">
                {grid.map((vehicle) => (
                  <MosaicCard key={vehicle.id} vehicle={vehicle} size="sm" />
                ))}
              </div>
            </div>
          ) : (
            <div
              className="wl7-new-arrivals__grid wl7-new-arrivals__grid--uniform"
              style={{ '--wl7-mosaic-cols': String(uniformCols) } as CSSProperties}
            >
              {grid.map((vehicle) => (
                <MosaicCard key={vehicle.id} vehicle={vehicle} size="sm" />
              ))}
            </div>
          )}

          {pageCount > 1 && (
            <div className="wl7-new-arrivals__nav">
              <button
                type="button"
                className="wl7-new-arrivals__prev"
                onClick={goPrev}
                aria-label={prevLabel}
                data-label={prevLabel}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                className="wl7-new-arrivals__next"
                onClick={goNext}
                aria-label={nextLabel}
                data-label={nextLabel}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>

        {pageCount > 1 && (
          <div className="wl7-new-arrivals__dots" role="tablist" aria-label={title}>
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === safePage}
                className={`wl7-new-arrivals__dot${i === safePage ? ' is-active' : ''}`}
                onClick={() => setPage(i)}
                aria-label={`Página ${i + 1}`}
              />
            ))}
          </div>
        )}

        <div className="wl7-new-arrivals__footer">
          <Link to={viewAllHref} className="wl7-new-arrivals__all">
            {viewAllLabel}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
